import { parseLocalDate, weekdayOf } from './localDate.js';
import { sortByDay } from './rrule.js';
import { lastSummaryDate, sortedMonthDays, summarizeRecurrence, } from './summary.js';
import { WEEKDAYS } from './types.js';
const ORD_KEY = {
    '1': 'ord1',
    '2': 'ord2',
    '3': 'ord3',
    '4': 'ord4',
    '5': 'ord5',
    '-1': 'ordLast',
    '-2': 'ordLast2',
    '-3': 'ordLast3',
    '-4': 'ordLast4',
    '-5': 'ordLast5',
};
const WORKDAYS = ['MO', 'TU', 'WE', 'TH', 'FR'];
function sameSet(a, b) {
    if (a.length !== b.length)
        return false;
    const s = new Set(a);
    return b.every((x) => s.has(x));
}
function sortedDays(days) {
    return [...new Set(days)].sort((a, b) => WEEKDAYS.indexOf(a) - WEEKDAYS.indexOf(b));
}
function upperFirst(text) {
    return text.length > 0 ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}
function phrases(lang) {
    const list = (items) => {
        if (items.length <= 1)
            return items[0] ?? '';
        return lang.text('listAnd', {
            a: items.slice(0, -1).join(lang.text('listSep')),
            b: items[items.length - 1] ?? '',
        });
    };
    const day = (w) => lang.weekday(WEEKDAYS.indexOf(w));
    const days = (ws) => list(sortedDays(ws).map(day));
    const ordinal = (o) => {
        const key = ORD_KEY[String(o)];
        return key ? lang.text(key) : lang.text('ordN', { n: Math.abs(o) });
    };
    const period = (interval, unit) => {
        if (interval === 1)
            return lang.text(`every${unit}`);
        if (interval === 2)
            return lang.text(`other${unit}`);
        return lang.text(`n${unit}s`, { count: interval });
    };
    const everyDays = (ws) => {
        const sorted = sortedDays(ws);
        if (sameSet(sorted, WEEKDAYS))
            return lang.text('everyDay');
        if (sameSet(sorted, WORKDAYS))
            return lang.text('everyWorkday');
        return lang.text('everyWeekdays', { days: days(sorted) });
    };
    const ordinalDays = (input) => {
        const byWeekday = new Map();
        const order = [];
        for (const d of sortByDay(input)) {
            const list = byWeekday.get(d.weekday);
            const ord = d.ordinal ?? 0;
            if (list)
                list.push(ord);
            else {
                byWeekday.set(d.weekday, [ord]);
                order.push(d.weekday);
            }
        }
        return list(order.map((w) => {
            const ords = (byWeekday.get(w) ?? []).sort((a, b) => (a < 0 ? 100 - a : a) - (b < 0 ? 100 - b : b));
            if (ords.some((o) => o === 0))
                return lang.text('anyWeekday', { weekday: day(w) });
            return lang.text('ordinalWeekday', { ordinals: list(ords.map(ordinal)), weekday: day(w) });
        }));
    };
    const monthDay = (v) => {
        if (v === -1)
            return lang.text('lastDay');
        if (v < -1)
            return lang.text('nthLastDay', { ordinal: ordinal(v) });
        return lang.text('monthDay', { day: v });
    };
    return { list, day, days, period, everyDays, ordinalDays, monthDay };
}
function baseSummary(input, lang) {
    const rule = input.rule;
    if (!rule)
        return lang.text('once');
    const p = phrases(lang);
    const interval = rule.interval ?? 1;
    const start = input.startDate ? parseLocalDate(input.startDate) : null;
    const plainDays = (rule.byDay ?? []).filter((d) => d.ordinal === undefined).map((d) => d.weekday);
    const ordinals = (rule.byDay ?? []).filter((d) => d.ordinal !== undefined);
    switch (rule.freq) {
        case 'DAILY': {
            if (plainDays.length > 0 && !sameSet(plainDays, WEEKDAYS)) {
                return interval === 1
                    ? p.everyDays(plainDays)
                    : lang.text('dailyOnlyOn', {
                        period: p.period(interval, 'Day'),
                        days: p.days(plainDays),
                    });
            }
            return p.period(interval, 'Day');
        }
        case 'WEEKLY': {
            const days = plainDays.length > 0 ? plainDays : input.startDate ? [weekdayOf(input.startDate)] : [];
            if (interval === 1)
                return days.length > 0 ? p.everyDays(days) : lang.text('everyWeek');
            const period = p.period(interval, 'Week');
            if (days.length === 0)
                return period;
            if (sameSet(days, WEEKDAYS))
                return lang.text('weeklyAllDays', { period });
            if (sameSet(days, WORKDAYS))
                return lang.text('weeklyWorkdays', { period });
            return lang.text('weeklyOn', { period, days: p.days(days) });
        }
        case 'MONTHLY': {
            if (ordinals.length > 0 && !(rule.byMonthDay && rule.byMonthDay.length > 0)) {
                const text = p.ordinalDays(ordinals);
                return interval === 1
                    ? lang.text('monthlyOrdinal', { ordinals: text })
                    : lang.text('monthlyOrdinalPeriod', {
                        period: p.period(interval, 'Month'),
                        ordinals: text,
                    });
            }
            const monthDays = rule.byMonthDay && rule.byMonthDay.length > 0
                ? [...rule.byMonthDay]
                : start
                    ? [start.day]
                    : [];
            const period = p.period(interval, 'Month');
            let text = monthDays.length > 0
                ? lang.text('monthlyOn', {
                    period,
                    days: p.list(sortedMonthDays(monthDays).map(p.monthDay)),
                })
                : period;
            if (plainDays.length > 0)
                text = lang.text('ifWeekday', { text, days: p.days(plainDays) });
            return text;
        }
        case 'YEARLY': {
            const period = p.period(interval, 'Year');
            const monthList = rule.byMonth && rule.byMonth.length > 0 ? [...rule.byMonth] : start ? [start.month] : [];
            const months = p.list(monthList.sort((a, b) => a - b).map((m) => lang.month(m)));
            if (ordinals.length > 0) {
                return lang.text('yearlyOrdinal', { period, ordinals: p.ordinalDays(ordinals), months });
            }
            if (rule.byMonthDay && rule.byMonthDay.length > 0) {
                const days = sortedMonthDays(rule.byMonthDay);
                if (days.length === 1 && (days[0] ?? 0) > 0) {
                    return lang.text('yearlyOn', {
                        period,
                        date: lang.text('dayMonth', { day: days[0] ?? 1, month: months }),
                    });
                }
                return lang.text('yearlyDays', { period, days: p.list(days.map(p.monthDay)), months });
            }
            if (start) {
                return lang.text('yearlyOn', {
                    period,
                    date: lang.text('dayMonth', { day: start.day, month: months }),
                });
            }
            return period;
        }
    }
}
/**
 * Samenvatting in een taal. `lang = null` (of Nederlands): precies de vaste Nederlandse tekst van
 * summarizeRecurrence, zodat nl overal gelijk blijft.
 */
export function summarizeRecurrenceIn(input, options, lang) {
    if (!lang)
        return summarizeRecurrence(input, options);
    let text = baseSummary(input, lang);
    if (input.rule && input.weekParity) {
        text = lang.text(input.weekParity === 'even' ? 'evenWeeks' : 'oddWeeks', { text });
    }
    if (!options.allDay && options.startTime) {
        text = options.endTime
            ? lang.text('fromTo', { text, start: options.startTime, end: options.endTime })
            : lang.text('at', { text, time: options.startTime });
    }
    if (input.rule) {
        if (input.count !== undefined && input.count !== null) {
            text = lang.text('times', { text, count: input.count });
        }
        else if (input.until) {
            text = lang.text('until', { text, date: lang.date(lastSummaryDate(input, options)) });
        }
    }
    return upperFirst(text);
}
/**
 * Herhaling van een taak (eenvoudiger vorm, tasks/rrule.ts) als agenda-regel, voor dezelfde
 * samenvatting in elke taal. `startDate` geeft de weekdag of dag van de maand als die ontbreekt.
 */
export function taskRecurrenceSummaryIn(r, startDate, lang) {
    if (r === null)
        return upperFirst(lang.text('once'));
    const rule = r.freq === 'daily'
        ? { freq: 'DAILY', interval: r.interval }
        : r.freq === 'weekly'
            ? {
                freq: 'WEEKLY',
                interval: r.interval,
                ...(r.by_weekday && r.by_weekday.length > 0
                    ? { byDay: r.by_weekday.map((weekday) => ({ weekday })) }
                    : {}),
            }
            : {
                freq: 'MONTHLY',
                interval: r.interval,
                ...(r.by_month_day !== undefined ? { byMonthDay: [r.by_month_day] } : {}),
            };
    return summarizeRecurrenceIn({ rule, startDate }, {}, lang);
}
//# sourceMappingURL=summaryLang.js.map